import { useState } from "react";
import type { SynthConfigParams as SynthParams } from "../../project/schema";

type Waveform = SynthParams["waveform"];
import { useProject, useServices } from "../context";
import { Panel, Select, Slider, Toggle, hz, ms, pct } from "./controls";
import { PresetBar } from "./PresetBar";
import { SampledControls } from "./SampledControls";
import { SoundPicker } from "./SoundPicker";

const WAVES: { value: Waveform; label: string }[] = [
  { value: "sine", label: "Sine" }, { value: "triangle", label: "Triangle" }, { value: "sawtooth", label: "Saw" }, { value: "square", label: "Square" },
];

/** Exponential mapping for frequency sliders (perceptually even). */
const toCut = (x: number) => Math.round(40 * Math.pow(18000 / 40, x));
const fromCut = (hzv: number) => Math.log(hzv / 40) / Math.log(18000 / 40);

/** A melodic track's sound: synthesizer controls, or a recorded instrument. */
export function SynthPanel({ trackId, title }: { trackId: string; title: string }) {
  const { controller } = useServices();
  const track = useProject((p) => p.tracks.find((t) => t.id === trackId));
  const [advanced, setAdvanced] = useState(false);
  if (!track) return null;
  if (track.instrument.kind !== "synth") {
    return (
      <Panel title={title}>
        <SoundPicker trackId={trackId} />
        <SampledControls trackId={trackId} />
      </Panel>
    );
  }
  const p = track.instrument.params;
  const set = (patch: Partial<SynthParams>) => controller.updateProject((proj) => ({
    ...proj,
    tracks: proj.tracks.map((t) => (t.id === trackId && t.instrument.kind === "synth" ? { ...t, instrument: { ...t.instrument, params: { ...t.instrument.params, ...patch } } } : t)),
  }));

  return (
    <Panel title={title} actions={<Toggle pressed={advanced} onChange={setAdvanced} title="Show all parameters">Advanced</Toggle>}>
      <SoundPicker trackId={trackId} />
      <PresetBar trackId={trackId} kind="synth" />
      <div className="controls-grid">
        <Select label="Waveform" value={p.waveform} options={WAVES} onChange={(waveform) => set({ waveform })} />
        <Slider label="Volume" value={p.volume} min={0} max={1} format={pct} onChange={(volume) => set({ volume })} />
        <Slider label="Cutoff" value={fromCut(p.cutoffHz)} min={0} max={1} step={0.001} format={(x) => hz(toCut(x))} onChange={(x) => set({ cutoffHz: toCut(x) })} hint="Low-pass filter cutoff: lower is darker" />
        <Slider label="Resonance" value={p.resonance} min={0.1} max={20} step={0.1} format={(v) => v.toFixed(1)} onChange={(resonance) => set({ resonance })} hint="Emphasis at the cutoff frequency" />
        <Slider label="Attack" value={p.attackSec} min={0.001} max={2} step={0.001} format={ms} onChange={(attackSec) => set({ attackSec })} hint="Time to reach full level" />
        <Slider label="Release" value={p.releaseSec} min={0.01} max={5} step={0.01} format={ms} onChange={(releaseSec) => set({ releaseSec })} hint="Fade time after the key is released" />
        {advanced && (
          <>
            <Slider label="Decay" value={p.decaySec} min={0.01} max={3} step={0.01} format={ms} onChange={(decaySec) => set({ decaySec })} hint="Time to fall to the sustain level" />
            <Slider label="Sustain" value={p.sustain} min={0} max={1} format={pct} onChange={(sustain) => set({ sustain })} hint="Level held while the key is down" />
            <Select label="Osc 2 wave" value={p.osc2Waveform} options={WAVES} onChange={(osc2Waveform) => set({ osc2Waveform })} />
            <Slider label="Osc 2 mix" value={p.osc2Mix} min={0} max={1} format={pct} onChange={(osc2Mix) => set({ osc2Mix })} />
            <Slider label="Osc 2 detune" value={p.osc2DetuneCents} min={-1200} max={1200} step={1} format={(v) => `${v > 0 ? "+" : ""}${v} cents`} onChange={(osc2DetuneCents) => set({ osc2DetuneCents })} />
            <Slider label="Max voices" value={p.maxVoices} min={1} max={32} step={1} format={(v) => String(v)} onChange={(maxVoices) => set({ maxVoices })} />
          </>
        )}
      </div>
    </Panel>
  );
}
